/** Cosmetics, battle pass, season, ranks and quests – numbers 1:1 from the prototype. */
import { HERO } from './heroes.js';

export interface Cardback { id: string; name: string; src: string; css: string }
export interface Felt { id: string; name: string; src: string; hi: string; base: string; edge: string }
export interface Frame { id: string; name: string; src: string }
export interface Title { id: string; name: string; src: string }
export interface ExtraEmote { id: string; text: string; src: string }

export const CARDBACKS: Cardback[] = [
  { id: 'tavern', name: 'Tavernenschild', src: 'Standard', css: 'repeating-linear-gradient(45deg,#7a4a22 0 6px,#5a3418 6px 12px)' },
  { id: 'oak', name: 'Eichenmaserung', src: 'Battlepass Stufe 5', css: 'repeating-linear-gradient(90deg,#6b4423 0 3px,#835530 3px 7px,#5b3a1e 7px 9px)' },
  { id: 'runes', name: 'Runenstein', src: 'Battlepass Stufe 20', css: 'radial-gradient(circle at 50% 50%,#6a7a90 0 3px,transparent 4px) 0 0/16px 16px,linear-gradient(135deg,#3a4250,#20262e)' },
  { id: 'stars', name: 'Sternenkarte', src: 'Premium Stufe 8', css: 'radial-gradient(#fff 1px,transparent 1.5px) 0 0/12px 12px,radial-gradient(#8fb0ff 1.4px,transparent 2px) 6px 6px/24px 24px,#142040' },
  { id: 'dragon', name: 'Drachenschuppe', src: 'Premium Stufe 18', css: 'radial-gradient(circle at 0 0,#9a2a2a 25%,transparent 26%) 0 0/14px 14px,radial-gradient(circle at 100% 100%,#9a2a2a 25%,transparent 26%) 0 0/14px 14px,#4a0f0f' },
  { id: 'brew', name: 'Brauerzeichen', src: 'Premium Stufe 30', css: 'radial-gradient(circle,#f6cf7a 0 6px,transparent 7px) center/100% 100% no-repeat,repeating-conic-gradient(#7a4a22 0 15deg,#5a3418 15deg 30deg)' },
  { id: 'gold', name: 'Goldfiligran', src: 'Rang Diamant', css: 'repeating-linear-gradient(45deg,#f6cf7a 0 2px,transparent 2px 10px),repeating-linear-gradient(-45deg,#f6cf7a 0 2px,transparent 2px 10px),#7a5010' },
];

export const FELTS: Felt[] = [
  { id: 'green', name: 'Flaschengrün', src: 'Standard', hi: '#276046', base: '#1c4634', edge: '#0f2a1f' },
  { id: 'wine', name: 'Burgunder', src: 'Battlepass Stufe 15', hi: '#6a2a3a', base: '#4a1a28', edge: '#2a0c14' },
  { id: 'night', name: 'Mitternacht', src: 'Premium Stufe 1', hi: '#2a3a6a', base: '#1a2648', edge: '#0c1226' },
  { id: 'moss', name: 'Waldmoos', src: 'Premium Stufe 12', hi: '#4a6a2a', base: '#334a1c', edge: '#1a2a0c' },
  { id: 'royal', name: 'Königspurpur', src: 'Premium Stufe 26', hi: '#5a2a6a', base: '#3e1a4a', edge: '#220c2a' },
];

export const FRAMES: Frame[] = [
  { id: 'wood', name: 'Holzrahmen', src: 'Standard' },
  { id: 'bronze', name: 'Bronzerahmen', src: 'Premium Stufe 4' },
  { id: 'silver', name: 'Silberrahmen', src: 'Battlepass Stufe 25' },
  { id: 'gold', name: 'Goldrahmen', src: 'Premium Stufe 22' },
  { id: 'dragon', name: 'Drachenrahmen', src: 'Rang Legende' },
];

export const TITLES: Title[] = [
  { id: 'neuling', name: 'Neuling', src: 'Standard' },
  { id: 'stammgast', name: 'Stammgast', src: 'Battlepass Stufe 10' },
  { id: 'flussmeister', name: 'Flussmeister', src: 'Battlepass Stufe 30' },
  { id: 'kartenhai', name: 'Kartenhai', src: 'Premium Stufe 14' },
  { id: 'bluffkoenig', name: 'Bluffkönig(in)', src: 'Premium Stufe 24' },
  { id: 'legende', name: 'Tavernenlegende', src: 'Rang Legende' },
];

export const EXTRA_EMOTES: ExtraEmote[] = [
  { id: 'prost', text: 'Prost! 🍺', src: 'Battlepass Stufe 2' },
  { id: 'autsch', text: 'Autsch, das tat weh.', src: 'Battlepass Stufe 18' },
  { id: 'bluff', text: 'Das war kein Bluff. Oder doch?', src: 'Premium Stufe 3' },
  { id: 'glueck', text: 'Glück gehabt … diesmal.', src: 'Premium Stufe 16' },
  { id: 'wirt', text: 'Wirt! Noch eine Runde!', src: 'Premium Stufe 28' },
];

// ---------- Battle pass ----------
export const XP_PER_TIER = 800;
export const PASS_TIERS = 30;

export type Reward =
  | { type: 'taler'; amount: number }
  | { type: 'cardback'; id: string }
  | { type: 'felt'; id: string }
  | { type: 'frame'; id: string }
  | { type: 'title'; id: string }
  | { type: 'emote'; id: string }
  | { type: 'skin'; hero: string; id: string };

export interface PassRow { tier: number; free: Reward; premium: Reward }

export const PASS: PassRow[] = (() => {
  const F: Record<number, Reward> = {
    1: { type: 'taler', amount: 60 }, 2: { type: 'emote', id: 'prost' }, 3: { type: 'taler', amount: 60 },
    5: { type: 'cardback', id: 'oak' }, 7: { type: 'taler', amount: 80 }, 10: { type: 'title', id: 'stammgast' },
    12: { type: 'taler', amount: 80 }, 15: { type: 'felt', id: 'wine' }, 18: { type: 'emote', id: 'autsch' },
    20: { type: 'cardback', id: 'runes' }, 22: { type: 'taler', amount: 100 }, 25: { type: 'frame', id: 'silver' },
    28: { type: 'taler', amount: 120 }, 30: { type: 'title', id: 'flussmeister' },
  };
  const P: Record<number, Reward> = {
    1: { type: 'felt', id: 'night' }, 2: { type: 'skin', hero: 'brunhild', id: 'winter' }, 3: { type: 'emote', id: 'bluff' },
    4: { type: 'frame', id: 'bronze' }, 6: { type: 'skin', hero: 'grokk', id: 'iron' }, 8: { type: 'cardback', id: 'stars' },
    10: { type: 'skin', hero: 'pippa', id: 'garden' }, 12: { type: 'felt', id: 'moss' }, 14: { type: 'title', id: 'kartenhai' },
    16: { type: 'emote', id: 'glueck' }, 18: { type: 'cardback', id: 'dragon' }, 20: { type: 'skin', hero: 'faelan', id: 'dawn' },
    22: { type: 'frame', id: 'gold' }, 24: { type: 'title', id: 'bluffkoenig' }, 26: { type: 'felt', id: 'royal' },
    27: { type: 'skin', hero: 'aldric', id: 'night' }, 28: { type: 'emote', id: 'wirt' }, 29: { type: 'skin', hero: 'zizzle', id: 'copper' },
    30: { type: 'cardback', id: 'brew' },
  };
  const rows: PassRow[] = [];
  for (let t = 1; t <= PASS_TIERS; t++) rows.push({ tier: t, free: F[t] ?? { type: 'taler', amount: 40 }, premium: P[t] ?? { type: 'taler', amount: 90 } });
  return rows;
})();

export function rewardName(r: Reward): string {
  switch (r.type) {
    case 'taler': return r.amount + ' Taler';
    case 'cardback': return 'Kartenrücken: ' + CARDBACKS.find((c) => c.id === r.id)!.name;
    case 'felt': return 'Tisch: ' + FELTS.find((c) => c.id === r.id)!.name;
    case 'frame': return 'Rahmen: ' + FRAMES.find((c) => c.id === r.id)!.name;
    case 'title': return 'Titel: ' + TITLES.find((c) => c.id === r.id)!.name;
    case 'emote': return 'Emote: „' + EXTRA_EMOTES.find((c) => c.id === r.id)!.text + '“';
    case 'skin': return HERO[r.hero]!.name.split(' ')[0] + '-Skin: ' + HERO[r.hero]!.skins.find((s) => s.id === r.id)!.name;
  }
}

export function rewardIcon(r: Reward): string {
  return { taler: '🪙', cardback: '🂠', felt: '🟢', frame: '⭕', title: '📜', emote: '💬', skin: '🎭' }[r.type];
}

// ---------- Season & ranks ----------
export interface Season { num: number; name: string; end: Date }

/** All defined seasons, ascending. When one ends, the next becomes active;
 * after the last defined season it simply stays active until more are added. */
export const SEASONS: Season[] = [
  { num: 1, name: 'Der Herbstmarkt', end: new Date('2026-11-30T23:59:59+01:00') },
  { num: 2, name: 'Wintersonnenwende', end: new Date('2027-02-28T23:59:59+01:00') },
];

export function currentSeason(now: Date = new Date()): Season {
  for (const s of SEASONS) if (now.getTime() <= s.end.getTime()) return s;
  return SEASONS[SEASONS.length - 1]!;
}

/** Season-end reward in taler per final rank. Proposal aligned with the
 * existing economy (tournament win = 60 taler, premium pass = 950): a season
 * spans ~3 months, so the top rank pays out roughly ten tournament wins
 * without gifting a whole premium pass. */
export const SEASON_REWARD_TALER: Record<string, number> = {
  bronze: 50, silber: 100, gold: 150, platin: 250, diamant: 400, legende: 600,
};

export interface Rank { id: string; name: string; min: number; color: string }
/** Thresholds use growing gaps (200/250/300/350 ≈ 4–8 first-place finishes at
 * +45) so each promotion feels earned; Bronze→Silber stays a quick first win. */
export const RANKS: Rank[] = [
  { id: 'bronze', name: 'Bronze', min: 0, color: '#b5733a' },
  { id: 'silber', name: 'Silber', min: 1100, color: '#c7ccd6' },
  { id: 'gold', name: 'Gold', min: 1300, color: '#f0c15c' },
  { id: 'platin', name: 'Platin', min: 1550, color: '#8fd3d0' },
  { id: 'diamant', name: 'Diamant', min: 1850, color: '#8fb0ff' },
  { id: 'legende', name: 'Legende', min: 2200, color: '#ff8a5c' },
];

export function rankFor(r: number): Rank {
  let out = RANKS[0]!;
  for (const k of RANKS) if (r >= k.min) out = k;
  return out;
}

/** Rating delta per placement, keyed by table size. */
export const PLACE_DELTA: Record<number, number[]> = { 6: [45, 25, 10, -10, -20, -30], 3: [30, 0, -25] };

// ---------- Quests ----------
export interface QuestDef { id: string; text: string; goal: number; key: string; xp: number; taler: number }
export const QUEST_POOL: QuestDef[] = [
  { id: 'hands15', text: 'Spiele 15 Hände', goal: 15, key: 'hands', xp: 300, taler: 40 },
  { id: 'win5', text: 'Gewinne 5 Hände', goal: 5, key: 'wins', xp: 300, taler: 40 },
  { id: 'flush', text: 'Gewinne einen Showdown mit Flush oder besser', goal: 1, key: 'bighand', xp: 400, taler: 60 },
  { id: 'steal3', text: 'Gewinne 3 Hände ohne Showdown', goal: 3, key: 'steals', xp: 300, taler: 40 },
  { id: 'top3', text: 'Beende ein Turnier in den Top 3', goal: 1, key: 'top3', xp: 400, taler: 60 },
  { id: 'emote2', text: 'Setze 2 Emotes am Tisch ein', goal: 2, key: 'emotes', xp: 200, taler: 30 },
  { id: 'allin', text: 'Gewinne eine Hand, in der du All-in warst', goal: 1, key: 'allinwin', xp: 350, taler: 50 },
];

/** Weekly quests: same progress keys as the daily pool, but week-sized goals.
 * Rewards ≈ 3–4 daily quests so a week of play pays out noticeably more. */
export const WEEKLY_QUEST_POOL: QuestDef[] = [
  { id: 'w_hands60', text: 'Spiele 60 Hände', goal: 60, key: 'hands', xp: 1000, taler: 140 },
  { id: 'w_win25', text: 'Gewinne 25 Hände', goal: 25, key: 'wins', xp: 1200, taler: 160 },
  { id: 'w_top3x3', text: 'Beende 3 Turniere in den Top 3', goal: 3, key: 'top3', xp: 1200, taler: 180 },
  { id: 'w_bighand3', text: 'Gewinne 3 Showdowns mit Flush oder besser', goal: 3, key: 'bighand', xp: 1400, taler: 200 },
  { id: 'w_steal10', text: 'Gewinne 10 Hände ohne Showdown', goal: 10, key: 'steals', xp: 1000, taler: 140 },
  { id: 'w_allin3', text: 'Gewinne 3 Hände, in denen du All-in warst', goal: 3, key: 'allinwin', xp: 1200, taler: 160 },
];

// ---------- Tournament structure ----------
export const BLINDS: [number, number][] = [
  [10, 20], [15, 30], [25, 50], [40, 80], [60, 120], [100, 200],
  [150, 300], [200, 400], [300, 600], [500, 1000], [800, 1600], [1200, 2400],
];
export const START_CHIPS = 1500;
